/**
 * Client-side price re-sort for collection grids that render one tile per variant.
 *
 * Shopify sorts a collection at the PRODUCT level, but this theme expands each product
 * into several per-variant tiles that all inherit the parent product's single sorted
 * position. That makes an expensive variant appear next to its cheap sibling when sorting
 * by price. This script reorders the individual tiles by their own variant price
 * (data-sort-price, in cents) whenever the active sort is price ascending/descending.
 *
 * Only product-card tiles are reordered; any other children (e.g. editorial tiles) stay
 * in their existing slots so curated layouts are preserved.
 *
 * Caveat: this reorders only the tiles present on the current page. With pagination, a
 * cheap variant on page 2 cannot move onto page 1 — the ordering is correct within each
 * page. Increase "products per page" if you want the whole collection ordered together.
 */
(function () {
  const PRICE_SORTS = {
    'price-ascending': 1,
    'price-descending': -1,
  };

  // Returns +1 for price-ascending, -1 for price-descending, 0 for any other sort.
  // Reads the currently selected sort option from the DOM first (this reflects the
  // collection's DEFAULT sort even when there is no sort_by in the URL), and falls back
  // to the sort_by URL parameter.
  function currentPriceDirection(scope) {
    const root = scope && scope.querySelector ? scope : document;
    const selected = root.querySelector('[role="option"][aria-selected="true"][value]');
    if (selected) {
      const value = selected.getAttribute('value');
      if (PRICE_SORTS[value]) return PRICE_SORTS[value];
      // A non-price sort is explicitly selected: never reorder.
      return 0;
    }
    const sortBy = new URLSearchParams(window.location.search).get('sort_by');
    return sortBy && PRICE_SORTS[sortBy] ? PRICE_SORTS[sortBy] : 0;
  }

  function priceOf(card) {
    const raw = card.getAttribute('data-sort-price');
    const value = parseInt(raw, 10);
    return Number.isFinite(value) ? value : null;
  }

  function sortList(list, direction) {
    const children = Array.from(list.children);
    const cards = children.filter(
      (child) => child.matches('product-card') && child.hasAttribute('data-sort-price')
    );
    if (cards.length < 2) return;

    const sorted = cards.slice().sort((a, b) => {
      const pa = priceOf(a);
      const pb = priceOf(b);
      // Cards without a valid price sink to the end, preserving relative order otherwise.
      if (pa === null && pb === null) return 0;
      if (pa === null) return 1;
      if (pb === null) return -1;
      if (pa === pb) return 0;
      return (pa - pb) * direction;
    });

    // Already in order? Do nothing (avoids needless DOM work / reflow).
    let changed = false;
    for (let i = 0; i < cards.length; i += 1) {
      if (cards[i] !== sorted[i]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;

    // Rebuild the child order: keep every non-card node exactly where it is, and fill the
    // positions that held product-cards with the sorted cards, in order. Building the full
    // sequence first and appending via a fragment avoids the pitfalls of moving live nodes
    // one-by-one.
    let cardIndex = 0;
    const fragment = document.createDocumentFragment();
    for (const child of children) {
      if (cards.includes(child)) {
        fragment.appendChild(sorted[cardIndex]);
        cardIndex += 1;
      } else {
        fragment.appendChild(child);
      }
    }
    list.appendChild(fragment);
  }

  function run() {
    const direction = currentPriceDirection();
    if (!direction) return;
    document.querySelectorAll('product-list').forEach((list) => sortList(list, direction));
  }

  // Debounce so a burst of DOM mutations (e.g. the AJAX section swap) triggers one sort.
  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  }

  function init() {
    run();

    // The collection updates in place on filter/sort (the theme replaces the section's
    // children and updates the URL via history.replaceState, without firing a "done"
    // event). Watch for product-list content changing and re-sort when it does.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (
            node.nodeType === 1 &&
            (node.matches?.('product-list') || node.querySelector?.('product-list'))
          ) {
            schedule();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
